/**
 * CSV & Excel Parser Utility
 * CSV, Excel 파일 및 텍스트 데이터를 표 형식으로 변환
 */
import * as XLSX from 'xlsx';

/**
 * CSV 문자열을 파싱하여 테이블 데이터로 변환
 * @param {string} csvString - CSV 형식 문자열
 * @param {object} options - 파싱 옵션
 * @returns {object} { headers: string[], rows: string[][] }
 */
export const parseCSV = (csvString, options = {}) => {
  const {
    delimiter = 'auto', // 'auto', ',', '\t', ';'
    hasHeader = true,
    trimValues = true,
  } = options;

  if (!csvString || typeof csvString !== 'string') {
    return { headers: [], rows: [] };
  }

  // 줄바꿈 정규화
  const normalizedString = csvString.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalizedString.split('\n').filter(line => line.trim());

  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }

  // 구분자 자동 감지
  const detectedDelimiter = delimiter === 'auto'
    ? detectDelimiter(lines[0])
    : delimiter;

  // 각 줄 파싱
  const parsedRows = lines.map(line => {
    const values = parseLine(line, detectedDelimiter);
    return trimValues ? values.map(v => v.trim()) : values;
  });

  // 헤더와 데이터 분리
  if (hasHeader && parsedRows.length > 0) {
    const headers = parsedRows[0];
    const rows = parsedRows.slice(1);
    return { headers, rows };
  }

  // 헤더 없는 경우 자동 생성
  const maxCols = Math.max(...parsedRows.map(r => r.length));
  const headers = Array.from({ length: maxCols }, (_, i) => `열${i + 1}`);
  return { headers, rows: parsedRows };
};

/**
 * CSV 한 줄을 파싱 (따옴표 처리 포함)
 */
const parseLine = (line, delimiter) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (inQuotes) {
      if (char === '"' && nextChar === '"') {
        // 이스케이프된 따옴표
        current += '"';
        i++;
      } else if (char === '"') {
        // 따옴표 끝
        inQuotes = false;
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === delimiter) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }
  }
  values.push(current);

  return values;
};

/**
 * 구분자 자동 감지
 */
const detectDelimiter = (line) => {
  const delimiters = [',', '\t', ';', '|'];
  const counts = delimiters.map(d => ({
    delimiter: d,
    count: (line.match(new RegExp(d === '|' ? '\\|' : d, 'g')) || []).length
  }));

  // 가장 많이 나타나는 구분자 선택
  counts.sort((a, b) => b.count - a.count);
  return counts[0].count > 0 ? counts[0].delimiter : ',';
};

/**
 * 텍스트에서 테이블 데이터 감지 및 파싱
 * @param {string} text - 일반 텍스트
 * @returns {object|null} 테이블 데이터 또는 null
 */
export const parseTextToTable = (text) => {
  if (!text || typeof text !== 'string') {
    return null;
  }

  const lines = text.trim().split('\n').filter(line => line.trim());

  if (lines.length < 2) {
    return null;
  }

  // 패턴 1: 탭 또는 여러 공백으로 구분된 데이터
  const spaceDelimited = tryParseSpaceDelimited(lines);
  if (spaceDelimited) {
    return spaceDelimited;
  }

  // 패턴 2: 콜론 또는 등호로 구분된 키-값 쌍
  const keyValuePairs = tryParseKeyValue(lines);
  if (keyValuePairs) {
    return keyValuePairs;
  }

  // 패턴 3: 숫자가 포함된 규칙적인 패턴
  const numericPattern = tryParseNumericPattern(lines);
  if (numericPattern) {
    return numericPattern;
  }

  return null;
};

/**
 * 공백/탭으로 구분된 데이터 파싱
 */
const tryParseSpaceDelimited = (lines) => {
  // 각 줄을 여러 공백 또는 탭으로 분할
  const rows = lines.map(line =>
    line.split(/\t+|\s{2,}/).map(v => v.trim()).filter(v => v)
  );

  // 모든 행의 열 수가 비슷한지 확인 (±1)
  const colCounts = rows.map(r => r.length);
  const avgCols = Math.round(colCounts.reduce((a, b) => a + b, 0) / colCounts.length);

  if (avgCols < 2) {
    return null;
  }

  const consistent = colCounts.every(c => Math.abs(c - avgCols) <= 1);
  if (!consistent) {
    return null;
  }

  // 첫 줄이 헤더인지 확인 (숫자가 적으면 헤더)
  const firstRowNumeric = rows[0].filter(v => /^\d/.test(v)).length;
  const hasHeader = firstRowNumeric < rows[0].length / 2;

  if (hasHeader) {
    return {
      headers: rows[0],
      rows: rows.slice(1).map(r => {
        // 열 수 맞추기
        while (r.length < rows[0].length) r.push('');
        return r.slice(0, rows[0].length);
      })
    };
  }

  // 자동 헤더 생성
  const headers = Array.from({ length: avgCols }, (_, i) => `열${i + 1}`);
  return { headers, rows };
};

/**
 * 키-값 쌍 파싱 (예: "매출: 67조")
 */
const tryParseKeyValue = (lines) => {
  const pairs = [];
  const pattern = /^(.+?)[\s]*[:=][\s]*(.+)$/;

  for (const line of lines) {
    const match = line.match(pattern);
    if (match) {
      pairs.push([match[1].trim(), match[2].trim()]);
    }
  }

  // 80% 이상이 키-값 형태면 테이블로 변환
  if (pairs.length >= lines.length * 0.8 && pairs.length >= 2) {
    return {
      headers: ['항목', '값'],
      rows: pairs
    };
  }

  return null;
};

/**
 * 숫자 패턴 데이터 파싱
 */
const tryParseNumericPattern = (lines) => {
  // 각 줄에서 숫자와 텍스트 추출
  const rows = [];

  for (const line of lines) {
    // 회사명 + 숫자들 패턴: "삼성전자 67조 6.5조"
    const parts = line.split(/\s+/).filter(v => v);
    if (parts.length >= 2) {
      // 숫자가 포함된 부분이 있는지 확인
      const hasNumbers = parts.some(p => /\d/.test(p));
      if (hasNumbers) {
        rows.push(parts);
      }
    }
  }

  if (rows.length < 2) {
    return null;
  }

  // 열 수 일관성 확인
  const colCounts = rows.map(r => r.length);
  const maxCols = Math.max(...colCounts);

  // 열 수 맞추기
  const normalizedRows = rows.map(r => {
    while (r.length < maxCols) r.push('');
    return r;
  });

  // 자동 헤더 추론 시도
  const headers = inferHeaders(normalizedRows);

  return {
    headers,
    rows: normalizedRows
  };
};

/**
 * 헤더 자동 추론
 */
const inferHeaders = (rows) => {
  if (rows.length === 0) return [];

  const numCols = rows[0].length;
  const headers = [];

  for (let i = 0; i < numCols; i++) {
    const colValues = rows.map(r => r[i]);

    // 첫 번째 열은 보통 이름/항목
    if (i === 0) {
      const allText = colValues.every(v => !/^\d+/.test(v));
      headers.push(allText ? '항목' : `열${i + 1}`);
    } else {
      // 숫자 포함 여부로 판단
      const hasNumbers = colValues.some(v => /\d/.test(v));
      if (hasNumbers) {
        // 단위 추론 (조, 억, %, 등)
        const units = colValues.map(v => {
          if (/조/.test(v)) return '(조)';
          if (/억/.test(v)) return '(억)';
          if (/%/.test(v)) return '(%)';
          return '';
        }).filter(u => u)[0] || '';
        headers.push(`값${i}${units}`);
      } else {
        headers.push(`열${i + 1}`);
      }
    }
  }

  return headers;
};

/**
 * File 객체에서 CSV 파싱
 */
export const parseCSVFile = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('파일이 없습니다'));
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target.result;
        const tableData = parseCSV(content);
        resolve(tableData);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('파일을 읽는 중 오류가 발생했습니다'));
    };

    // UTF-8 먼저 시도, 실패시 EUC-KR
    reader.readAsText(file, 'UTF-8');
  });
};

/**
 * File 객체에서 Excel 파싱 (.xlsx, .xls)
 */
export const parseExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('파일이 없습니다'));
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // 첫 번째 시트 사용
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // 시트를 JSON 배열로 변환 (헤더 포함)
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length === 0) {
          resolve({ headers: [], rows: [] });
          return;
        }

        // 첫 번째 행을 헤더로, 나머지를 데이터로
        const headers = jsonData[0].map(h => String(h || '').trim());
        const rows = jsonData.slice(1).map(row =>
          headers.map((_, i) => String(row[i] ?? '').trim())
        );

        // 빈 행 제거
        const filteredRows = rows.filter(row => row.some(cell => cell !== ''));

        resolve({ headers, rows: filteredRows });
      } catch (error) {
        reject(new Error('Excel 파일을 파싱하는 중 오류가 발생했습니다: ' + error.message));
      }
    };

    reader.onerror = () => {
      reject(new Error('파일을 읽는 중 오류가 발생했습니다'));
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * 파일 확장자에 따라 적절한 파서 선택
 */
export const parseSpreadsheetFile = async (file) => {
  if (!file) {
    throw new Error('파일이 없습니다');
  }

  const fileName = file.name.toLowerCase();

  if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
    return parseExcelFile(file);
  } else if (fileName.endsWith('.csv') || fileName.endsWith('.txt')) {
    return parseCSVFile(file);
  } else {
    throw new Error('지원하지 않는 파일 형식입니다. CSV 또는 Excel 파일을 사용해주세요.');
  }
};

/**
 * 테이블 데이터 유효성 검증
 */
export const validateTableData = (tableData) => {
  if (!tableData || typeof tableData !== 'object') {
    return { valid: false, error: '올바른 데이터 형식이 아닙니다' };
  }

  if (!Array.isArray(tableData.headers) || tableData.headers.length === 0) {
    return { valid: false, error: '헤더가 없습니다' };
  }

  if (!Array.isArray(tableData.rows)) {
    return { valid: false, error: '데이터 행이 없습니다' };
  }

  return { valid: true };
};

/**
 * 텍스트가 테이블 데이터인지 빠르게 판단
 * 기사/문장 텍스트와 표 데이터를 구분하기 위해 엄격한 기준 적용
 */
export const isTableLikeText = (text) => {
  if (!text || typeof text !== 'string') return false;

  const lines = text.trim().split('\n').filter(l => l.trim());
  if (lines.length < 2) return false;

  // 1. 긴 문장이 포함되어 있으면 기사로 판단 (표가 아님)
  // 한 줄에 50자 이상이고, 마침표/물음표/느낌표로 끝나면 문장임
  const hasSentences = lines.some(l =>
    l.length > 50 && /[.?!。]$/.test(l.trim())
  );
  if (hasSentences) return false;

  // 2. 평균 줄 길이가 너무 길면 기사로 판단 (표는 보통 짧은 셀)
  const avgLineLength = lines.reduce((sum, l) => sum + l.length, 0) / lines.length;
  if (avgLineLength > 80) return false;

  // 3. 탭으로 구분된 데이터 확인 (가장 확실한 표 형식)
  const tabDelimitedLines = lines.filter(l => l.includes('\t'));
  if (tabDelimitedLines.length >= lines.length * 0.8) {
    // 탭 개수가 일관적인지 확인
    const tabCounts = tabDelimitedLines.map(l => (l.match(/\t/g) || []).length);
    const firstTabCount = tabCounts[0];
    const consistent = tabCounts.every(c => Math.abs(c - firstTabCount) <= 1);
    if (consistent && firstTabCount >= 1) return true;
  }

  // 4. CSV 형식 확인 (쉼표로 구분되고, 열 수가 일관적인 경우만)
  const commaLines = lines.filter(l => l.includes(','));
  if (commaLines.length >= lines.length * 0.8) {
    const commaCounts = commaLines.map(l => (l.match(/,/g) || []).length);
    const firstCommaCount = commaCounts[0];
    // 쉼표 개수가 일관적이고, 최소 2개 이상이어야 표
    const consistent = commaCounts.every(c => Math.abs(c - firstCommaCount) <= 1);
    if (consistent && firstCommaCount >= 2) return true;
  }

  // 5. 세미콜론이나 파이프로 구분된 데이터
  const hasStructuredDelimiters = lines.every(l => /[;|]/.test(l));
  if (hasStructuredDelimiters) {
    const delimCounts = lines.map(l => (l.match(/[;|]/g) || []).length);
    const consistent = delimCounts.every(c => Math.abs(c - delimCounts[0]) <= 1);
    if (consistent && delimCounts[0] >= 1) return true;
  }

  // 6. 여러 공백(2개 이상)으로 구분된 열이 일관된 경우
  const multiSpaceLines = lines.filter(l => /\s{2,}/.test(l));
  if (multiSpaceLines.length >= lines.length * 0.8) {
    const colCounts = multiSpaceLines.map(l => l.split(/\s{2,}/).filter(v => v.trim()).length);
    const consistent = colCounts.every(c => Math.abs(c - colCounts[0]) <= 1);
    // 최소 3개 이상의 열이 있어야 표로 판단
    if (consistent && colCounts[0] >= 3) return true;
  }

  return false;
};

export default {
  parseCSV,
  parseCSVFile,
  parseExcelFile,
  parseSpreadsheetFile,
  parseTextToTable,
  validateTableData,
  isTableLikeText,
};
